import { useCopyAgendaTemplate } from "@/hooks/useCopyAgendaTemplate";
import { useUserStore } from "@/stores/userStore";
import type { Agenda } from "@repo/types";
import { Button } from "@repo/ui/components/Base/Button";
import { DialogBody, DialogFooter } from "@repo/ui/components/Base/Dialog";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { Formik } from "formik";
import * as yup from "yup";

export interface AgendaTemplateCopyFormProps {
  tenantId: string;
  template: Agenda;
  handleCancel: () => void;
  handleComplete: () => void;
  withinDialog?: boolean;
}

const validationSchema = yup.object({
  teamId: yup.string().required("Team is required"),
});

export const AgendaTemplateCopyForm = ({
  tenantId,
  template,
  handleCancel,
  handleComplete,
  withinDialog,
}: AgendaTemplateCopyFormProps) => {
  const copyAgendaTemplate = useCopyAgendaTemplate(tenantId);

  const availableTeams = useUserStore((s) => s.availableTeams);

  return (
    <div className="w-full">
      <Formik
        initialValues={{ teamId: "" }}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          await copyAgendaTemplate.copyAsync(template, values.teamId);
          handleComplete();
        }}
      >
        {({
          values,
          handleSubmit,
          setFieldValue,
          isSubmitting,
          errors,
          touched,
        }) => {
          const footer = (
            <>
              <Button
                variant="destructive"
                type="button"
                disabled={isSubmitting}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                Copy
              </Button>
            </>
          );

          const form = (
            <div className="flex flex-col gap-y-4">
              <div className="flex flex-col">
                <SelectInput
                  label="*Team"
                  value={values.teamId}
                  placeholder="Select a team"
                  onValueChange={(value) => setFieldValue("teamId", value)}
                  options={availableTeams.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.name}
                    </SelectItem>
                  ))}
                  orientation="vertical"
                  error={!!errors.teamId && touched.teamId}
                  errorMessage={errors.teamId}
                />
              </div>
              {!withinDialog && (
                <div className="flex flex-row justify-between">{footer}</div>
              )}
            </div>
          );

          return (
            <form onSubmit={handleSubmit}>
              {withinDialog ? (
                <>
                  <DialogBody>{form}</DialogBody>
                  <DialogFooter className="justify-between">
                    {footer}
                  </DialogFooter>
                </>
              ) : (
                form
              )}
            </form>
          );
        }}
      </Formik>
    </div>
  );
};
